/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import { tableV2StackedCellContentSx } from '../../../components/v2/tableV2Styles';
import * as actions from '../../../store/jornada/actions';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import * as selectorsAuth from '../../../store/auth/reducer';
import { FOLHA_PONTO_FULL_ACCESS, JORNADA } from 'constants/modules';
import { hasModuleConfiguration } from '../../../helpers/moduleConfigurationPermission';
import { formatWithDay } from 'utils/date-format';
import { canEditJornada, jornadaListUrl, readJornadaFormId, JORNADA_FORM_ID_NOVO } from '../../../helpers/jornadaHelpers';
import {
  getJornadaHoraExtraDisplay,
  getJornadaValidadoDisplay,
  rowHasHoraExtra,
} from '../../../helpers/jornadaValidado';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import Formulario from './Formulario';

const LIST_PATH = '/v2/jornada';

const hasPermission = (moduleConfigurations, authData) =>
  hasModuleConfiguration(moduleConfigurations, authData, FOLHA_PONTO_FULL_ACCESS);

const getDefaultFilter = (moduleConfigurations, authData) => ({
  inicio: null,
  fim: null,
  tipoControle: 'FOLHA_PONTO',
  usuarioId: !hasPermission(moduleConfigurations, authData) ? [authData.id] : [],
});

const readEpochDate = (value, boundary) => {
  if (!value) return null;
  const parsed = moment(value, 'YYYY-MM-DD', true);
  if (!parsed.isValid()) return null;
  return boundary === 'end' ? parsed.endOf('day').valueOf() : parsed.startOf('day').valueOf();
};

const readListFilterFromSearch = (search, fallback) => {
  const q = new URLSearchParams(search || '');
  const next = { ...fallback };
  const inicio = readEpochDate(q.get('inicio'), 'start');
  const fim = readEpochDate(q.get('fim'), 'end');
  if (inicio != null) next.inicio = inicio;
  if (fim != null) next.fim = fim;
  const usuarioId = q.get('usuarioId');
  if (usuarioId) next.usuarioId = usuarioId.split(',').map((id) => id.trim()).filter(Boolean);
  return next;
};

const sameIdList = (a, b) => {
  const left = Array.isArray(a) ? a.map(String) : [];
  const right = Array.isArray(b) ? b.map(String) : [];
  return left.length === right.length && left.every((id, index) => id === right[index]);
};

const JornadaV2 = ({
  authData,
  exportJornadas,
  deleteJornada,
  moduleConfigurations,
  history,
  location,
}) => {
  const useNovoFluxo = hasModuleConfiguration(moduleConfigurations, authData, JORNADA);

  useEffect(() => {
    if (!useNovoFluxo) {
      history.replace('/v2/folha-pontos');
    }
  }, [useNovoFluxo, history]);

  const [localFilter, setLocalFilter] = useState(() =>
    readListFilterFromSearch(location.search, getDefaultFilter(moduleConfigurations, authData)));

  useEffect(() => {
    const q = new URLSearchParams(location.search || '');
    if (!q.get('inicio') && !q.get('fim') && !q.get('usuarioId')) return;
    setLocalFilter((current) => {
      const next = readListFilterFromSearch(location.search, current);
      if (
        next.inicio === current.inicio
        && next.fim === current.fim
        && sameIdList(next.usuarioId, current.usuarioId)
      ) return current;
      return next;
    });
  }, [location.search]);
  const [openExport, setOpenExport] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);

  const { open, formId } = readJornadaFormId(location.search);

  const adminPermission = hasPermission(moduleConfigurations, authData);

  const defaultFilterMemo = useMemo(
    () => getDefaultFilter(moduleConfigurations, authData),
    [moduleConfigurations, authData],
  );

  const apiFilter = useMemo(
    () => ({ ...localFilter, tipoControle: 'FOLHA_PONTO' }),
    [localFilter],
  );

  const onEdit = (rowId) => {
    history.push(jornadaListUrl(LIST_PATH, location.search, rowId));
  };

  const onAdd = () => {
    history.push(jornadaListUrl(LIST_PATH, location.search, JORNADA_FORM_ID_NOVO));
  };

  const onClose = (isSave) => {
    history.push(jornadaListUrl(LIST_PATH, location.search, null));
    if (isSave) {
      setReload((current) => !current);
    }
  };

  const rowCanEdit = (row) => canEditJornada(row, authData?.id, adminPermission);

  const getParceiroNome = (row) =>
    row?.inspetorAuxiliarNome || row?.inspetorAuxiliarName || row?.inspetorAuxiliar?.nome || '';

  const rowActions = [
    {
      key: 'view',
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      title: 'Visualizar',
      onClick: (rowId) => onEdit(rowId),
      show: (row) => !rowCanEdit(row),
    },
    {
      key: 'edit',
      icon: <EditOutlinedIcon fontSize="small" />,
      title: 'Editar',
      onClick: (rowId) => onEdit(rowId),
      show: (row) => rowCanEdit(row),
    },
  ];

  const columns = [
    {
      field: 'data',
      headerName: 'Data',
      flex: 0.5,
      renderCell: (params) => formatWithDay(params.row.data),
    },
    { field: 'ocorrenciaNome', headerName: 'Ocorrência', flex: 0.6 },
    {
      field: 'inspetorNome',
      headerName: 'Usuário e Parceiro',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => {
        const parceiroNome = getParceiroNome(params.row);
        return (
          <Box sx={tableV2StackedCellContentSx}>
            <Typography variant="body2" noWrap>
              <Box component="span" sx={{ fontWeight: 600 }}>Usuário: </Box>
              {params.row.inspetorNome}
            </Typography>
            {parceiroNome ? (
              <Typography variant="body2" noWrap>
                <Box component="span" sx={{ fontWeight: 600 }}>Parceiro: </Box>
                {parceiroNome}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    { field: 'jornadaTotal', headerName: 'Total da Jornada', flex: 0.5 },
    {
      field: 'horaExtraAutorizadoPorNome',
      headerName: 'Hora Extra',
      flex: 0.6,
      renderCell: (params) => {
        const display = getJornadaHoraExtraDisplay(params.row);
        return (
          <Box sx={{ ...tableV2StackedCellContentSx, alignItems: 'flex-start', gap: 0.25 }}>
            {renderStatus(display)}
            {display.autorizadoPorNome ? (
              <Typography variant="body2" noWrap>
                {display.autorizadoPorNome}
              </Typography>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'validado',
      headerName: 'Validado',
      flex: 0.55,
      renderCell: (params) => renderStatus(
        getJornadaValidadoDisplay(params.row.validado, rowHasHoraExtra(params.row)),
      ),
    },
  ];

  if (!useNovoFluxo) return null;

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Folha de Ponto</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="jornadas/filter"
          paginationUrl="/v2/jornada"
          filterData={apiFilter}
          refreshKey={reload}
          columns={columns}
          rowHeight={56}
          actions={rowActions}
          onDelete={(rowId) => deleteJornada(rowId, () => setReload((c) => !c))}
          canDelete={(row) => rowCanEdit(row)}
          headerActions={[
            {
              label: 'Novo',
              onClick: onAdd,
              startIcon: <AddIcon />,
              variant: 'contained',
            },
            {
              label: 'Exportar',
              onClick: () => setOpenExport(true),
              startIcon: <FileDownloadOutlinedIcon />,
              variant: 'outlined',
            },
          ]}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilterMemo}
          appliedFilter={localFilter}
          fields={[
            { field: 'useDates' },
            {
              field: 'useUsuarios',
              md: 12,
              xs: 12,
              multiSelect: true,
              disabled: !adminPermission,
            },
            {
              field: 'useOption',
              md: 12,
              xs: 12,
              code: 'PONTO_OCORRENCIA',
              label: 'Ocorrências',
              key: 'ocorrenciaIds',
            },
          ]}
          onFilter={(filterData) => {
            const next = { ...filterData, tipoControle: 'FOLHA_PONTO' };
            setLocalFilter(next);
            setOpenFilter(false);
            const q = new URLSearchParams(location.search || '');
            if (next.inicio) q.set('inicio', moment(next.inicio).format('YYYY-MM-DD'));
            else q.delete('inicio');
            if (next.fim) q.set('fim', moment(next.fim).format('YYYY-MM-DD'));
            else q.delete('fim');
            const ids = Array.isArray(next.usuarioId) ? next.usuarioId.filter(Boolean) : [];
            if (ids.length) q.set('usuarioId', ids.join(','));
            else q.delete('usuarioId');
            const search = q.toString();
            const current = (location.search || '').replace(/^\?/, '');
            if (search !== current) {
              history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
            }
          }}
        />

        <Formulario
          id={formId}
          open={open}
          onClose={onClose}
          adminPermission={adminPermission}
        />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => exportJornadas(apiFilter, () => setOpenExport(false))}
          title="Confirmação"
          confirmMessage="Exportar"
        >
          <DialogContentText>
            Deseja exportar todas as informações filtradas?
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

JornadaV2.propTypes = {
  authData: PropTypes.object,
  exportJornadas: PropTypes.func.isRequired,
  deleteJornada: PropTypes.func.isRequired,
  moduleConfigurations: PropTypes.array,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actions,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(JornadaV2));
